import { useState, useEffect, useCallback } from "react";
import Cookies from "js-cookie";

export function useAuthData(apiBaseUrl) {
  const [authToken, setAuthToken] = useState("");
  const [loggedInUser, setLoggedInUser] = useState({});
  const [trainingProgram, setTrainingProgram] = useState(null);
  const [allTrainingPrograms, setAllTrainingPrograms] = useState(null);

  // Helper to save token and clear user state
  const saveAuthToken = useCallback((token) => {
    const isSecure = window.location.protocol === "https:";
    Cookies.set("token", token, {
      expires: 7,
      secure: isSecure,
      sameSite: isSecure ? "None" : "Lax",
    });
    setAuthToken(token);
    setLoggedInUser({});
  }, []);

  // 1. Load Token on Mount
  useEffect(() => {
    const token = Cookies.get("token");
    if (token && token.length > 0) {
      setAuthToken(token);
    }
  }, []);

  // 2. Fetch User & Programs when Token/URL exists
  useEffect(() => {
    if (!authToken || !apiBaseUrl) return;

    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      try {
        const headers = { Authorization: `Token ${authToken}` };

        // Fetch User
        const userResponse = await fetch(`${apiBaseUrl}/user`, { headers, signal });
        const userData = await userResponse.json();

        if (Object.keys(userData).includes("email")) {
          setLoggedInUser(userData);
        } else {
          setLoggedInUser({});
          saveAuthToken("");
          return; // Stop if auth invalid
        }

        // Fetch Programs (Parallel)
        const [allProgramsRes, subscribedRes] = await Promise.all([
          fetch(`${apiBaseUrl}/trainingprograms`, { headers, signal }),
          fetch(`${apiBaseUrl}/trainingprograms?show_subscribed=true`, { headers, signal })
        ]);

        const allPrograms = await allProgramsRes.json();
        setAllTrainingPrograms(allPrograms);

        const subscribedPrograms = await subscribedRes.json();
        if (subscribedPrograms.length > 0) {
          setTrainingProgram(subscribedPrograms[0]);
        }

      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching auth data:", error);
        }
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [authToken, apiBaseUrl, saveAuthToken]);

  return {
    authToken,
    saveAuthToken,
    loggedInUser,
    trainingProgram,
    allTrainingPrograms,
  };
}